/* eslint-disable react/jsx-no-bind */
import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { GlobeAltIcon } from '@heroicons/react/24/outline';
import { useProject } from '../../config/ProjectContext';
import { DATA_REGIONS, DATA_REGION_OPTIONS, DEFAULT_DATA_REGION } from '../../config/regions';
import canAccessChina from '../../utils/canAccessChina';
import useAnalytics from '../../hooks/useAnalytics';

/**
 * Data Region selector (feature 021-china-account-access).
 *
 * Lets authorized users switch between the Global and China data regions. The
 * China option is hidden entirely for users without the `china-access`
 * Cognito group. Selection is persisted via ProjectContext and propagated to
 * the backend through the `x-data-region` GraphQL header (see
 * configureAmplify).
 */
export default function RegionSelector() {
  const { region, setRegion } = useProject();
  const queryClient = useQueryClient();
  const { setAnalytics } = useAnalytics();
  const [chinaAllowed, setChinaAllowed] = useState(false);

  useEffect(() => {
    let mounted = true;
    canAccessChina().then((allowed) => {
      if (mounted) {
        setChinaAllowed(allowed);
        // Safety: if a stale China selection persists but access was revoked,
        // fall back to Global.
        if (!allowed && region === DATA_REGIONS.CHINA) {
          setRegion(DEFAULT_DATA_REGION);
        }
      }
    });
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = (value) => {
    if (value === region) return;

    setRegion(value);
    setAnalytics({ event: `header-data-region-select-${value}`, sectionId: 'header' });
    // Region-scoped data must be refetched against the new destination.
    queryClient.invalidateQueries();
  };

  const visibleOptions = DATA_REGION_OPTIONS.filter((option) => option.value !== DATA_REGIONS.CHINA || chinaAllowed);

  // Users without China access only have one region (Global); no selector needed.
  if (visibleOptions.length <= 1) return null;

  return (
    <div
      className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-gray-50 p-0.5"
      role="group"
      aria-label="Data region"
    >
      <GlobeAltIcon className="ml-1 h-4 w-4 text-gray-400" aria-hidden="true" />
      {visibleOptions.map((option) => {
        const isSelected = region === option.value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={isSelected}
            onClick={() => handleSelect(option.value)}
            className={`rounded px-3 py-1 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
              isSelected
                ? 'bg-indigo-600 text-white shadow-sm'
                : 'text-gray-700 hover:bg-gray-200'
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
